import { useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useMspBulkCreateTenants } from "@/hooks/use-api";
import { parseCsv, type CsvRow } from "@/lib/csv";
import type {
  MspBulkCreateClient,
  MspBulkCreateResponse,
} from "@/types/api";

interface BulkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// Client-side mirror of the backend validation (src/api/routes/msp.py). Kept in
// sync so the preview flags exactly what the API would reject; the backend stays
// the authority.
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PLANS = ["starter", "essential", "growth", "business", "enterprise"];

function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 59)
    .replace(/-$/, "");
}

interface PreviewRow {
  name: string;
  slug: string;
  plan: string;
  templateId?: string;
  error: string | null;
}

export function buildPreviewRow(raw: CsvRow): PreviewRow {
  const name = (raw.name ?? "").trim();
  let slug = (raw.slug ?? "").trim().toLowerCase();
  if (!slug && name) slug = slugify(name);
  const plan = (raw.plan ?? "").trim().toLowerCase() || "starter";
  const templateId = (raw.template_id ?? "").trim() || undefined;

  let error: string | null = null;
  if (!name) error = "name is required";
  else if (name.length > 200) error = "name must be under 200 characters";
  else if (!SLUG_RE.test(slug))
    error = "slug must be 3-63 lowercase alphanumeric characters or hyphens";
  else if (!PLANS.includes(plan)) error = `plan must be one of: ${PLANS.join(", ")}`;
  else if (templateId && !UUID_RE.test(templateId)) error = "invalid template_id";

  return { name, slug, plan, templateId, error };
}

function toClient(row: PreviewRow): MspBulkCreateClient {
  return {
    name: row.name,
    slug: row.slug,
    plan: row.plan,
    ...(row.templateId ? { template_id: row.templateId } : {}),
  };
}

export function BulkImportDialog({ open, onOpenChange }: BulkImportDialogProps) {
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [result, setResult] = useState<MspBulkCreateResponse | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const bulkMutation = useMspBulkCreateTenants();

  const validRows = rows.filter((r) => r.error === null);

  const handleFile = (file: File) => {
    setParseError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = parseCsv(String(reader.result ?? ""));
        setRows(parsed.map(buildPreviewRow));
        setFileName(file.name);
      } catch {
        setParseError("Could not read that file as CSV.");
        setRows([]);
      }
    };
    reader.onerror = () => setParseError("Could not read that file.");
    reader.readAsText(file);
  };

  const handleSubmit = () => {
    bulkMutation.mutate(validRows.map(toClient), {
      onSuccess: (data) => setResult(data),
    });
  };

  const downloadTemplate = () => {
    const csv = "name,slug,plan,template_id\nAcme GmbH,acme-gmbh,starter,\n";
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "clients-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setRows([]);
    setFileName(null);
    setParseError(null);
    setResult(null);
    bulkMutation.reset();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleClose = () => {
    if (!bulkMutation.isPending) {
      reset();
      onOpenChange(false);
    }
  };

  // --- Results screen ---
  if (result) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Import Results</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm">
              <span className="font-medium text-green-400">{`${result.created_count} created`}</span>
              {result.failed_count > 0 && (
                <span className="font-medium text-red-400">{` · ${result.failed_count} failed`}</span>
              )}
            </p>
            <div className="max-h-72 overflow-y-auto rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {result.results.map((r) => (
                    <TableRow key={r.index}>
                      <TableCell>{r.name}</TableCell>
                      <TableCell className="font-mono text-xs">{r.slug}</TableCell>
                      <TableCell>
                        {r.status === "created" ? (
                          <span className="text-green-400">
                            Created{r.warning ? ` (${r.warning})` : ""}
                          </span>
                        ) : (
                          <span className="text-red-400">{r.error}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={reset}>
                Import another
              </Button>
              <Button onClick={handleClose}>Done</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // --- Upload / preview screen ---
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk Import Clients</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              aria-label="CSV file"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFile(file);
              }}
              className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-sm"
            />
            <Button type="button" variant="outline" size="sm" onClick={downloadTemplate}>
              Download template
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Columns: <code className="font-mono">name,slug,plan,template_id</code>.
            Only <code className="font-mono">name</code> is required — slug is
            derived from the name when blank, plan defaults to{" "}
            <code className="font-mono">starter</code>. Clients are created without
            an admin user; invite admins afterward.
          </p>

          {parseError && (
            <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {parseError}
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="max-h-72 overflow-y-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Slug</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Template</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{row.name || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell className="font-mono text-xs">{row.slug || "—"}</TableCell>
                        <TableCell>{row.plan}</TableCell>
                        <TableCell className="font-mono text-xs">{row.templateId ?? "—"}</TableCell>
                        <TableCell>
                          {row.error ? (
                            <span className="text-red-400">{row.error}</span>
                          ) : (
                            <span className="text-green-400">Ready</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-sm text-muted-foreground">
                {`${validRows.length} of ${rows.length} row${rows.length === 1 ? "" : "s"} ready to import${fileName ? ` · ${fileName}` : ""}.`}
                {validRows.length < rows.length && " Invalid rows are skipped."}
              </p>
            </>
          )}

          {bulkMutation.isError && (
            <div id="bulk-import-error" role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-400">
              {(bulkMutation.error as { message?: string })?.message ??
                "Import failed. Please try again."}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={bulkMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={validRows.length === 0 || bulkMutation.isPending}
              aria-describedby={bulkMutation.isError ? "bulk-import-error" : undefined}
            >
              {bulkMutation.isPending
                ? "Importing..."
                : `Import ${validRows.length || ""} client${validRows.length === 1 ? "" : "s"}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
